// DEL-1125: side-effect import. Publishes this build's identity onto a global
// so scripts/verify-deployed-sdk.js can prove the served bundle is the build
// the deploy just rsynced. Imported here rather than from a route because
// posthog.js is already guaranteed to be in a served client chunk — the same
// chunks the marker scan already reads. Do not convert this to a named import:
// an unused export is a tree-shaking candidate, a globalThis write is not.
import './deploy-stamp';
import { isTestAccountEmail } from '../utils/testAccounts';
import { getCookieConsent, getAnalyticsConsentDecision } from '../contexts/CookieConsentContext';
import { safeUrl } from '../utils/safe-url';
import { getPageAnalyticsProps } from '../lib/pageAnalyticsProps';

let isPostHogInitialized = false;

let posthogInstance = null;

const getPosthog = async () => {
  if (!posthogInstance) {
    const mod = await import('posthog-js');
    posthogInstance = mod.posthog;
  }
  return posthogInstance;
};

/**
 * True when the app runs on a developer machine. Analytics must never start
 * here: local dev-server errors (for example axios disconnects) were shipped
 * into the production project and kept reopening a triaged exception issue.
 */
export const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.local')
  );
};

/**
 * Replace credential values in a URL string with `REDACTED`. Thin alias over
 * the shared client/src/utils/safe-url helper so the same deny list applies at
 * every URL-bearing sink (PostHog, ErrorBoundary, anything future). See
 * safe-url.ts for the canonical SENSITIVE_URL_KEYS list and rationale.
 */
export const maskUrl = safeUrl;

/**
 * Browser notifications that arrive as unhandled errors but are not faults.
 *
 * "ResizeObserver loop completed with undelivered notifications." (and its
 * older Chrome spelling, "ResizeObserver loop limit exceeded") is dispatched
 * by the browser itself when a resize callback changes layout, so delivery
 * needs another pass than the spec's depth limit allows. The outstanding
 * notifications are delivered on the very next frame: nothing is dropped and
 * nothing is broken. There is no Error object behind it, which is why these
 * reach PostHog `synthetic: true` with zero stack frames, app or vendor.
 *
 * We do not construct a single ResizeObserver - every one belongs to
 * Chart.js's responsive resize handling (and Headless UI's anchoring), so
 * there is no call site here to correct. Left unfiltered it lands in Error
 * tracking as an unhandled, high-severity issue on /dashboard and /assets
 * and buries real exceptions, which is the same failure mode the isLocalHost
 * guard above already exists to prevent.
 *
 * "No Listener: tabs:outgoing.message.ready" is a browser extension's
 * message bridge (webext-bridge style) rejecting a promise in our page when
 * its own background listener is missing. Delphina sends no extension
 * messages, so there is nothing to fix here; it reached PostHog via the
 * DEL-1081 non-Error guard and produced 80 events from one Safari session in
 * a day, burying the real errors in that session.
 */
const BENIGN_EXCEPTION_PATTERNS = [
  /^ResizeObserver loop\b/,
  /^No Listener: tabs:/,
];

/**
 * Expected auth-state rejections from the monitored-axios layer and from the
 * browser's own network-failure console messages.
 *
 * monitoredAxios.ts's response interceptor catches every non-2xx and logs
 * `console.error(...)` so the failure shows up in the browser console
 * (`RequestMonitor`) and in PostHog via `capture_console_errors`. A 401 on
 * any of `/api/trial/*` (the only place that calls these today) means the
 * user's access token has expired and the Supabase refresh failed - they
 * are effectively signed out and the auth context is already redirecting
 * them to /login. Same goes for 403 from the double-submit CSRF check,
 * which the response interceptor recovers from by retrying once with a
 * freshly captured token. Both are auth state transitions, not bugs.
 *
 * Left unfiltered they pile up as a single high-volume fingerprint in
 * Error Tracking (DEL-634: 5 events in one second from a logged-out
 * landing-page hit) and bury real exceptions. The shape that reaches
 * PostHog is the structured `$exception_list` entry, where `type` carries
 * the exception `name` (e.g. `"AxiosError"`) and `value` carries the bare
 * `message` (e.g. `"Request failed with status code 401"`) - PostHog does
 * NOT prepend the type to the value. The two `{ type, value }` co-key
 * entries below match the production shape: the `type` guard makes sure a
 * future code change that emits the bare `"Request failed with status
 * code 401"` string from a non-auth path still reaches PostHog, which is
 * the conservative direction.
 *
 * The same auth-state-not-bug semantics apply when GoTrueClient
 * `refreshSession()` throws `AuthSessionMissingError` because the user
 * is signed out and there is no session to refresh. That error carries
 * `name: "AuthSessionMissingError"` and `message: "Auth session missing!"`.
 * Same `{ type, value }` shape as the AxiosError entry above; same
 * conservative-direction guarantee on the value regex.
 *
 * The two value-only entries (`{ value: RegExp, type: undefined }`) cover
 * auth-state console messages which carry no JS exception `name` at all:
 *
 * 1. The browser auto-emits `Failed to load resource: the server responded
 *    with a status of 401 (Unauthorized)` (and the 403 spelling) for every
 *    failed network request. PostHog's `capture_console_errors: true`
 *    forwards it as a `$exception` with an empty `type`. Same auth-state
 *    semantics as the AxiosError above, same suppression need. The regex is
 *    status-narrow (not just `Failed to load resource.*\d{3}`) so a real
 *    404 / 500 / 503 still surfaces. The URL is intentionally not in the
 *    regex: a 401/403 from a non-auth endpoint is still an auth-state signal
 *    worth suppressing here (the browser only emits this message on actual
 *    HTTP failure statuses, never on 2xx).
 *
 * 2. `client/src/utils/monitoredAxios.ts:358` logs
 *    `[RequestMonitor] <source>: Failed <METHOD> <url> - <status>` via
 *    `console.error(...)`. PostHog's autocapture forwards it the same way
 *    as the browser-native message. The shape carries the source token
 *    (`general`, `<feature>`, etc.), so the regex anchors on `[RequestMonitor]`
 *    at the start and on the `- 401`/`- 403` suffix at the end - the status
 *    is what makes this auth-state rather than a real failure. A `- 500`
 *    variant reaches PostHog unchanged so we keep seeing server incidents.
 */
const BENIGN_AUTH_FAILURE_PATTERNS = [
  {
    type: /AxiosError/,
    value: /Request failed with status code (401|403)\b/,
  },
  {
    type: /AuthSessionMissingError/,
    value: /^Auth session missing!?$/,
  },
  {
    type: undefined,
    value: /^Failed to load resource[^\n]*(?:\bstatus of (401|403)\b|\b(401|403)\b\s+(?:Unauthorized|Forbidden)\b)/,
  },
  {
    type: undefined,
    value: /^\[RequestMonitor\][^\n]*-\s*(401|403)\b/,
  },
];

/**
 * True when a single (type, value) entry matches one of
 * `BENIGN_AUTH_FAILURE_PATTERNS`. Supports three pattern shapes:
 *
 * - `RegExp` - value-only. Matches the entry's value field with no type
 *   check. Reserved for filters whose value regex is uniquely identifying
 *   (no current entry uses this shape; the legacy AxiosError value-only
 *   filter was removed in DEL-909 because its `"AxiosError: "` value-prefix
 *   assumption did not match PostHog's real `$exception_list` shape).
 * - `{ type: RegExp, value: RegExp }` - both the entry's type and value
 *   must match. Used for the axios-thrown AxiosError and
 *   AuthSessionMissingError patterns. An entry without a type (the flat
 *   `$exception_values` case) cannot satisfy a `{type, value}` pattern -
 *   the entry is left unmatched and the event is kept, which is the
 *   conservative direction.
 * - `{ type: undefined, value: RegExp }` - value-only with an explicit
 *   `type: undefined`. Used for the browser-native `Failed to load
 *   resource` and `[RequestMonitor]` console messages, which arrive in
 *   PostHog with no `type` field at all (the autocapture of console output
 *   does not synthesise an exception `name`). The explicit `undefined`
 *   shape is kept distinct from the bare-`RegExp` shape so a future reader
 *   can tell "I really meant value-only, with the document anchor" from
 *   "I forgot to wrap it in `{ value: ... }`".
 */
const matchesAuthFailurePattern = (entryType, entryValue) =>
  BENIGN_AUTH_FAILURE_PATTERNS.some((pattern) => {
    if (pattern instanceof RegExp) {
      return pattern.test(entryValue);
    }
    if (
      pattern &&
      pattern.value instanceof RegExp &&
      pattern.type === undefined
    ) {
      return pattern.value.test(entryValue);
    }
    if (
      pattern &&
      pattern.type instanceof RegExp &&
      pattern.value instanceof RegExp
    ) {
      return (
        typeof entryType === 'string' &&
        pattern.type.test(entryType) &&
        pattern.value.test(entryValue)
      );
    }
    return false;
  });

/**
 * True only when an exception event carries at least one message and *every*
 * message is benign. A genuine error chained alongside a ResizeObserver one
 * still gets reported.
 */
const isBenignException = (props) => {
  if (!props) return false;

  const values = [
    ...(Array.isArray(props.$exception_values) ? props.$exception_values : []),
    ...(Array.isArray(props.$exception_list)
      ? props.$exception_list.map((entry) => entry?.value)
      : []),
  ].filter((value) => typeof value === 'string');

  return (
    values.length > 0 &&
    values.every((value) =>
      BENIGN_EXCEPTION_PATTERNS.some((pattern) => pattern.test(value))
    )
  );
};

/**
 * True only when every exception message matches a known-benign auth-failure
 * shape. Same "all messages must match" semantic as `isBenignException`: a
 * real exception chained with a 401 (rare, but possible from a custom
 * interceptor that rethrows wrapped errors) still gets reported.
 *
 * `$exception_list` carries the (type, value) pairs and is the primary
 * signal. The AxiosError and AuthSessionMissingError patterns use a
 * `{ type, value }` co-key (their `$exception_list` entry carries a JS
 * exception `name`); the browser-native `Failed to load resource` and
 * `[RequestMonitor]` patterns are value-only and the corresponding
 * `$exception_list` entry carries `type: undefined`. `matchesAuthFailurePattern`
 * accepts both shapes, so the listEntries branch alone covers the
 * production shape regardless of which pattern fires.
 *
 * `$exception_values` is a flat array of message strings with no type info.
 * It is only consulted when `$exception_list` is absent - in practice
 * PostHog populates both lists together, and trusting the structured list
 * avoids dropping events where the flat value happens to satisfy a
 * `{type, value}` pattern's value regex without the confirming type. The
 * flatValues branch also accepts the value-only `{ value, type: undefined }`
 * shape so the value-only patterns still match if the list is missing.
 */
const isBenignAuthFailure = (props) => {
  if (!props) return false;

  const listEntries = Array.isArray(props.$exception_list)
    ? props.$exception_list.filter((entry) => typeof entry?.value === 'string')
    : [];

  const flatValues = Array.isArray(props.$exception_values)
    ? props.$exception_values.filter((value) => typeof value === 'string')
    : [];

  if (listEntries.length === 0 && flatValues.length === 0) return false;

  for (const entry of listEntries) {
    if (!matchesAuthFailurePattern(entry.type, entry.value)) return false;
  }

  if (listEntries.length > 0) return true;

  for (const value of flatValues) {
    const matched = BENIGN_AUTH_FAILURE_PATTERNS.some((pattern) => {
      if (pattern instanceof RegExp) return pattern.test(value);
      if (pattern && pattern.value instanceof RegExp && pattern.type === undefined) {
        return pattern.value.test(value);
      }
      return false;
    });
    if (!matched) return false;
  }

  return true;
};

// Path that the OAuth callback redirects to. The page itself only exists to
// read the credential-bearing query/fragment, exchange it for a Supabase
// session, and redirect the user away. The path is a sensitive signal
// (a $pageview here confirms a successful auth transition), and the auto
// $pageview fires before AuthSuccessClient can run history.replaceState, so
// we drop every URL property that points at this path at the sink - the
// maskUrl deny list would only redact keys, not the path itself.
const AUTH_SUCCESS_PATH = '/auth-success';

const isAuthSuccessUrl = (value) => {
  if (typeof value !== 'string' || value === '') return false;
  try {
    const url = new URL(value);
    return url.pathname === AUTH_SUCCESS_PATH || url.pathname.startsWith(`${AUTH_SUCCESS_PATH}/`);
  } catch {
    return value.startsWith(AUTH_SUCCESS_PATH) || value.includes(`${AUTH_SUCCESS_PATH}?`) || value.includes(`${AUTH_SUCCESS_PATH}#`);
  }
};

/**
 * Sanitize each event before it leaves the browser. Drops anything captured on
 * a developer machine, drops benign browser notifications that are not real
 * exceptions, drops expected auth-state rejections (401/403 from the
 * monitored-axios layer), drops every event whose URL properties point at the
 * /auth-success OAuth callback (the path itself is sensitive), and masks known
 * credential query/fragment keys in any other URL property.
 */
export const beforeSendPostHog = (event) => {
  if (!event) return event;
  if (isLocalHost()) return null;

  if (event.event === '$exception' && isBenignException(event.properties)) {
    return null;
  }

  if (event.event === '$exception' && isBenignAuthFailure(event.properties)) {
    return null;
  }

  const props = event.properties;

  // Page-level properties (e.g. a /compare/ pair's audience) on the automatic
  // $pageview, so Web Analytics and Paths can be filtered by them too. The
  // custom page_view gets them in capturePageView.
  if (event.event === '$pageview' && props) {
    Object.assign(props, getPageAnalyticsProps());
  }

  if (props) {
    let urlSeen = false;
    for (const key of Object.keys(props)) {
      if (typeof props[key] !== 'string') continue;
      if (isAuthSuccessUrl(props[key])) {
        urlSeen = true;
        break;
      }
    }
    if (urlSeen) return null;

    for (const key of ['$current_url', '$referrer']) {
      if (typeof props[key] === 'string') {
        props[key] = maskUrl(props[key]);
      }
    }
  }
  return event;
};

/**
 * Test-only reset of module state so each test starts from a clean slate.
 * Not for production use.
 */
export const __resetPostHogState = () => {
  appliedDecision = null;
  isPostHogInitialized = false;
  nonErrorThrowGuardInstalled = false;
};

// DEL-1081: PostHog's `wrapOnError` reads `window.onerror`'s 5th arg verbatim
// and forwards it to `buildFromUnknown` as the exception value. Safari passes
// the literal thrown object through (`throw {}` or `Promise.reject({})`),
// and the resulting PostHog $exception_list entry carries the
// "Object captured as exception with keys: [object has no keys]" placeholder
// with no $exception_sources — exactly the two occurrences recorded on
// /pricing. After PostHog has installed its wrapper, we replace the wrapper
// with our own that detects a non-Error object (specifically the empty
// object pattern) and converts it into a real Error before reaching
// PostHog's buildFromUnknown, then re-emits via captureException so the
// stack traces to the re-wrap point. Real-Error values delegate to PostHog's
// preserved wrapper, so all other error capture behaviour is unchanged.
let nonErrorThrowGuardInstalled = false;

const isEmptyNonError = (value) =>
  value != null &&
  typeof value === 'object' &&
  !(value instanceof Error) &&
  Object.keys(value).length === 0;

const wrapNonError = (value, kind) => {
  const message =
    (typeof value === 'object' && (value.message || value.name)) ||
    `Non-Error ${kind} reached the global error handler`;
  const wrapped = new Error(
    `${message} [re-wrapped by client/src/analytics/posthog.js:installNonErrorThrowGuard — see DEL-1081]`,
    { cause: value }
  );
  return wrapped;
};

export const installNonErrorThrowGuard = (ph) => {
  if (nonErrorThrowGuardInstalled || typeof window === 'undefined') return;
  nonErrorThrowGuardInstalled = true;

  // PostHog's wrapOnError has already run by the time `loaded(ph)` fires,
  // so window.onerror IS the PostHog wrapper. Preserve it for the
  // real-Error passthrough path.
  const prevOnError = window.onerror;
  window.onerror = function (message, source, lineno, columnno, error) {
    if (isEmptyNonError(error)) {
      const wrapped = wrapNonError(error, 'throw');
      try {
        ph.captureException(wrapped, { $exception_handled: false });
      } catch {
        // Capture path itself failed - fall back to the existing wrapper so
        // the placeholder is still recorded (not silent).
        if (prevOnError) {
          return prevOnError.apply(this, arguments);
        }
      }
      // Suppress the browser's default error logging - we've already
      // captured.
      return true;
    }
    if (prevOnError) {
      return prevOnError.apply(this, arguments);
    }
    return false;
  };

  // PostHog's wrapUnhandledRejection has already run too. Same pattern.
  const prevOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = function (event) {
    const reason =
      event && typeof event === 'object' && 'reason' in event
        ? event.reason
        : event;
    if (isEmptyNonError(reason)) {
      const wrapped = wrapNonError(reason, 'rejection');
      try {
        ph.captureException(wrapped, { $exception_handled: false });
      } catch {
        if (prevOnUnhandledRejection) {
          return prevOnUnhandledRejection.apply(this, arguments);
        }
      }
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return true;
    }
    if (prevOnUnhandledRejection) {
      return prevOnUnhandledRejection.apply(this, arguments);
    }
    return false;
  };
};

export const __resetNonErrorThrowGuard = () => {
  nonErrorThrowGuardInstalled = false;
};

/**
 * Hostnames that get PostHog tracing headers (X-POSTHOG-SESSION-ID,
 * X-POSTHOG-DISTINCT-ID, X-POSTHOG-WINDOW-ID) on fetch/XHR. Only our own
 * origin and our own API host: adding a third party (Supabase, Stripe) would
 * send it our session id and could fail its CORS preflight.
 *
 * The server (setupExpressRequestContext) turns these into $session_id on
 * server-side events such as trial_started and onboarding_started, so they
 * can be joined to the landing page of the session that produced them.
 */
export const getTracingHostnames = () => {
  if (typeof window === 'undefined') return [];
  const hosts = new Set([window.location.hostname]);
  try {
    if (process.env.NEXT_PUBLIC_API_URL) {
      hosts.add(new URL(process.env.NEXT_PUBLIC_API_URL).hostname);
    }
  } catch {
    // A malformed API URL just means same-origin calls only.
  }
  return [...hosts].filter(Boolean);
};

/**
 * First-party cookie mirroring the current PostHog session id. Tracing headers
 * cover API calls, but top-level navigations such as the Google OAuth callback
 * (GET /auth/google/callback on the API) carry no custom headers; the server
 * reads this cookie as a fallback (server/middleware/posthogSessionContext.js).
 *
 * Written from PostHog's `loaded` callback, and only with analytics consent.
 * Shared with the API subdomain so the callback sees it.
 */
export const POSTHOG_SESSION_COOKIE = 'dph_ph_sid';
const POSTHOG_SESSION_COOKIE_MAX_AGE = 24 * 60 * 60; // PostHog's maximum session length

export const writePostHogSessionCookie = (sessionId) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  // PostHog now also loads for visitors who declined, in cookieless mode.
  // They must get no cookie from us either.
  if (!getCookieConsent('analytics')) return;
  if (!sessionId || !/^[A-Za-z0-9-]{8,100}$/.test(sessionId)) return;
  const { hostname, protocol } = window.location;
  const parts = [
    `${POSTHOG_SESSION_COOKIE}=${sessionId}`,
    'Path=/',
    `Max-Age=${POSTHOG_SESSION_COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (hostname === 'delphina.money' || hostname.endsWith('.delphina.money')) {
    parts.push('Domain=.delphina.money');
  }
  if (protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
};

export const initPostHog = async () => {
  if (typeof window === 'undefined' || isPostHogInitialized) {
    return;
  }

  if (isLocalHost()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: Skipping initialization on localhost');
    }
    return;
  }

  // Visitors who decline, and visitors who have not answered the banner yet,
  // get anonymous pageviews in cookieless mode: no cookies, no local or session
  // storage, no person profile, no session replay. PostHog derives a daily,
  // salted hash server-side instead of a client id. Requires cookieless mode to
  // be enabled in the PostHog project settings (switched on 29 Oct 2026).
  // Before 29 Oct 2026 PostHog did not load until the banner was answered, so
  // most search visitors were never counted.
  const decision = getAnalyticsConsentDecision();
  const consent = decision === 'granted';

  const posthog = await getPosthog();
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
    // Required once api_host is a reverse proxy (our own subdomain): without it
    // the toolbar and the recordings player link to the proxy, not PostHog.
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    cookieless_mode: 'on_reject',
    // With on_reject alone, a visitor whose consent is still pending is not
    // captured at all (posthog-js consent.js: pending counts as opted out but
    // not as rejected). Defaulting to opt-out makes pending count as rejected,
    // which on_reject captures cookieless with persistence disabled. It writes
    // nothing until opt_in_capturing / opt_out_capturing is called.
    opt_out_capturing_by_default: true,
    before_send: beforeSendPostHog,
    // Set explicitly so a future PostHog default change (or accidental
    // server-side flip) cannot silently disable error tracking. The default
    // is already true, but stating it here keeps intent visible and gives
    // Error Tracking a stable contract: every new issue we triage is
    // guaranteed to come through this code path.
    capture_exceptions: true,
    // Session recording (rrweb) is NOT subject to before_send - the
    // masking filter applies to $current_url/$referrer on events, but a
    // recording captures every DOM mutation including the brief window
    // where /auth-success#token=… was visible in the address bar and any
    // input value the user typed. Gate recording on the same analytics
    // consent that gates the rest of the capture. If a future consent
    // toggle changes the decision at runtime, call
    // posthog.sessionRecording({ disable: <new> }) from the consent-change
    // handler.
    disable_session_recording: !consent,
    // Belt-and-braces alongside the consent gate: even when recording is
    // on, rrweb must never start while the user is on the OAuth callback
    // page. The auto $pageview fires before AuthSuccessClient can run
    // history.replaceState, and the address bar briefly shows
    // /auth-success#token=… - both the path (auth-completion signal) and
    // the credential-bearing fragment are sensitive. before_send drops
    // pageview events on this URL too, but rrweb records DOM, not events,
    // so it needs its own blocklist. The regex matches the literal path
    // with any query/fragment after it.
    session_recording: {
      urlBlocklist: [{ url: '\\/auth-success(\\?|#|$)', matching: 'regex' }],
    },
    // $pageview is what PostHog's Web Analytics, Paths, bounce rate and
    // scroll-depth all key off. With it disabled, $pageleave arrived without
    // $prev_pageview_duration or $prev_pageview_max_content_percentage, so
    // there was no way to tell "never scrolled to the CTA" from "saw it and
    // ignored it". The custom `page_view` below still fires alongside it -
    // different event name, no collision - so the existing DB pipeline and
    // any historical queries against `page_view` are unaffected.
    //
    // 'history_change', not true. #469: with `true`, posthog-js captures a
    // $pageview on the INITIAL LOAD only. Every in-app route change after that
    // sent the custom `page_view` and nothing else, so Web Analytics, Paths,
    // bounce rate and scroll-depth saw roughly a quarter to a half of real page
    // views. Measured over 28 days: `/` 206 visits vs 83 $pageviews, `/login`
    // 139 vs 33, `/pricing` 55 vs 12 - and every rate built on PostHog page
    // views in this repo was understated by 2-4x.
    //
    // 'history_change' captures on the initial load AND after navigation.
    // It is also posthog-js's own default from 2026-05-24, so setting `true`
    // here was overriding the default and turning the history capture OFF.
    //
    // Verified against the installed posthog-js with the network blocked:
    //   capture_pageview: true            -> $pageview after load: 1, after a
    //                                         route change: 1 (no new event)
    //   capture_pageview: 'history_change' -> $pageview after load: 1, after a
    //                                         route change: 2 (one new event)
    capture_pageview: 'history_change',
    capture_pageleave: true,
    tracing_headers: getTracingHostnames(),
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') {
        ph.debug();
      }

      // DEL-1081: Safari passes a literal thrown object (`throw {}`,
      // `Promise.reject({})`) through window.onerror, and PostHog records it
      // as "Object captured as exception with keys: [object has no keys]"
      // with no stack. Wrap the handlers PostHog has just installed so
      // non-Error values become real Errors first. Must run here, after
      // PostHog's own wrappers. scripts/verify-deployed-sdk.js checks the
      // served bundle for this guard, so removing the call fails that check.
      installNonErrorThrowGuard(ph);

      // Keep the session cookie in step with PostHog's session id (fires now
      // and on every rotation) so OAuth redirects can be joined server-side.
      if (typeof ph.onSessionId === 'function') {
        ph.onSessionId((sessionId) => writePostHogSessionCookie(sessionId));
      }
    },
  });

  isPostHogInitialized = true;
  applyPostHogConsent(posthog, decision);

  if (process.env.NODE_ENV === 'development') {
    console.log(`PostHog initialized (${consent ? 'cookies' : 'cookieless'})`);
  }
};

let appliedDecision = null;

// In 'on_reject' mode, opting out switches PostHog to cookieless capture
// rather than stopping it; opting in restores cookies. Replay follows consent.
const applyPostHogConsent = (posthog, decision) => {
  if (decision === appliedDecision) return;
  appliedDecision = decision;
  if (decision === 'undecided') {
    // Cookieless by default (opt_out_capturing_by_default). Do not call
    // opt_out_capturing here: it stores the choice, and this visitor has not
    // made one.
    if (typeof posthog.stopSessionRecording === 'function') posthog.stopSessionRecording();
    return;
  }
  if (decision === 'granted') {
    posthog.opt_in_capturing();
    if (typeof posthog.startSessionRecording === 'function') posthog.startSessionRecording();
  } else {
    if (typeof posthog.stopSessionRecording === 'function') posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
};

/**
 * Bring PostHog in line with the current banner decision. Called on every
 * consent poll: initialises on the first decision, then switches between
 * cookies and cookieless when the visitor changes their mind.
 */
export const syncPostHogConsent = async () => {
  if (typeof window === 'undefined' || isLocalHost()) return;
  const decision = getAnalyticsConsentDecision();
  if (!isPostHogInitialized) {
    await initPostHog();
    return;
  }
  applyPostHogConsent(await getPosthog(), decision);
};

export const capturePostHogEvent = async (eventName, properties = {}, options = undefined) => {
  if (typeof window === 'undefined') return;

  // PostHog never starts on a developer machine (see isLocalHost), so this is
  // the only way to see what an event would have carried when testing locally.
  if (process.env.NODE_ENV === 'development') {
    console.log(`PostHog [dev]: "${eventName}"`, properties);
  }

  const consent = getCookieConsent('analytics');
  if (!consent) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`PostHog: Event "${eventName}" blocked due to lack of consent`);
    }
    return;
  }

  const posthog = await getPosthog();
  if (options) {
    posthog.capture(eventName, properties, options);
  } else {
    posthog.capture(eventName, properties);
  }
};

// Why a signed-in session ended. Every sign-out path calls this first so a
// user who gets bounced to the home page minutes after signing up leaves a
// trace: until now a forced sign-out (an API 401, or the page deciding there
// was no session) was indistinguishable from the user tapping Sign Out.
// Sent with sendBeacon because the page navigates away immediately after.
// One event per page load: the redirect that follows a sign-out would
// otherwise record a second, misleading reason.
let sessionEndRecorded = false;
export const trackSessionEnded = (reason, props = {}) => {
  if (sessionEndRecorded) return;
  sessionEndRecorded = true;
  let msSincePageLoad;
  try {
    msSincePageLoad = Math.round(performance.now());
  } catch {
    msSincePageLoad = undefined;
  }
  const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
  return capturePostHogEvent(
    'session_ended',
    { reason, pathname, ms_since_page_load: msSincePageLoad, ...props },
    { send_instantly: true, transport: 'sendBeacon' },
  );
};
// A new signed-in session can end too. Sign-in reaches /dashboard client-side,
// with no page load to clear the flag, so without this a sign-out after
// signing back in (or after a signed-out 401 already set the flag) was never
// recorded. AuthContext calls it whenever a user id appears.
export const markSessionStarted = () => {
  sessionEndRecorded = false;
};
export const __resetSessionEnded = markSessionStarted;

export const capturePostHogException = async (error) => {
  if (typeof window === 'undefined' || !getCookieConsent('analytics')) return;

  // A global-error fallback can replace the layout before AnalyticsTracker
  // mounts, so initialize the shared client before reporting the exception.
  await initPostHog();
  if (!isPostHogInitialized) return;

  const posthog = await getPosthog();
  posthog.captureException(error);
};

/**
 * Identify the signed-in user so PostHog attributes events to a person.
 *
 * `initPostHog` configures `person_profiles: 'identified_only'`, which means
 * PostHog creates a person record ONLY once identify() has been called. Until
 * that happens every event - including the whole auth funnel - lands on the
 * anonymous distinct_id and is unreachable from a person. With no identify()
 * call anywhere in the app, no person record was ever created at all.
 *
 * Consent-gated for the same reason capturePostHogEvent is: identifying a user
 * is personal data, and it must not happen for a visitor who declined
 * analytics. The consent check is repeated here rather than assumed, because
 * this is reachable from the auth paths where the user has just interacted
 * with the consent surface.
 *
 * Idempotent by design: PostHog's identify() is a no-op when the distinct_id
 * already matches, so the caller does not have to track whether it has run.
 */
export const setPostHogUserId = async (userId, email) => {
  if (typeof window === 'undefined' || !userId) return;
  if (!getCookieConsent('analytics')) return;
  const posthog = await getPosthog();
  // is_test_account lets PostHog's internal/test-user filter drop QA, agent
  // and staff accounts from funnels and signup counts.
  if (email) {
    posthog.identify(userId, { is_test_account: isTestAccountEmail(email) });
  } else {
    posthog.identify(userId);
  }
};

/**
 * The current PostHog session id, or undefined when PostHog has not started
 * (no analytics consent, local dev, SSR). Sent with auth and server-tracked
 * events so server-side events join the browser session that caused them -
 * without it a signup could not be tied to the page the visitor landed on.
 */
export const getPostHogSessionId = () => {
  if (typeof window === 'undefined') return undefined;
  if (!isPostHogInitialized || !posthogInstance) return undefined;
  try {
    const sid = posthogInstance.get_session_id();
    return typeof sid === 'string' && sid ? sid : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Drop the identified person on sign-out so a shared device does not attribute
 * the next person's events to whoever signed in before them. PostHog's
 * reset() returns the SDK to a fresh anonymous distinct_id.
 */
export const resetPostHogUser = async () => {
  if (typeof window === 'undefined') return;
  if (!getCookieConsent('analytics')) return;
  const posthog = await getPosthog();
  posthog.reset();
  // reset() clears the stored consent, and with opt_out_capturing_by_default
  // that drops a consenting visitor back to cookieless. PostHog's documented
  // order is reset first, then opt in again.
  if (typeof posthog.opt_in_capturing === 'function') posthog.opt_in_capturing();
};

export const capturePageView = (path, title) => {
  capturePostHogEvent('page_view', { path, title, ...getPageAnalyticsProps() });
};

export const trackSignUp = (method = 'email') => {
  capturePostHogEvent('sign_up', { method });
};

export const trackDashboardView = () => {
  capturePostHogEvent('dashboard_view');
};

export const trackFirstAssetAdded = (assetType, category) => {
  capturePostHogEvent('first_asset_added', { asset_type: assetType, category });
};

export const trackUpgradeClick = (triggerType) => {
  capturePostHogEvent('upgrade_click', { trigger_type: triggerType });
};

export const trackTrialStarted = () => {
  capturePostHogEvent('trial_started');
};

export const trackTrialExpired = () => {
  capturePostHogEvent('trial_expired');
};

export const captureError = (error, context) => {
  capturePostHogEvent('error', {
    error_message: error.message,
    error_stack: error.stack,
    context,
  });
};

/**
 * Auth funnel events. Three events, one each at: page view, attempt, success.
 *
 * The auth code already calls trackSignUp on attempt/success, but those are
 * funnel-blind (no `?ref=`, no method split). Without these three, the only
 * way to read "which marketing page actually drives a logged-in user" is to
 * join `cta_clicked` against `sign_up` by hand, which loses anyone whose
 * `?ref=` is missing or malformed. The events below are the funnel primary.
 */
export const trackLoginViewed = (props = {}) => {
  capturePostHogEvent('login_viewed', props);
};

/**
 * Record that the visitor tried to sign in.
 *
 * #470: the Google and Apple paths leave the page. `capturePostHogEvent`
 * awaits `getPosthog()` before it calls `posthog.capture`, so on the OAuth
 * paths the event was fired microseconds before a full page navigation and
 * the request was still in flight when the document unloaded. The browser
 * dropped it.
 *
 * The symptom was measurable and self-contradictory: `auth_succeeded` (93
 * people) exceeded `auth_attempted` (74 people), which is impossible if the
 * attempt is always recorded before the success. It was not a tracking bug in
 * the funnel logic. It was the attempt event not surviving the redirect.
 *
 * So `beacon: true` sends it with `sendBeacon`, which the browser holds until
 * the request completes even while the page is unloading. This is the same
 * mechanism `trackSessionEnded` already uses for the same reason - "Sent with
 * sendBeacon because the page navigates away immediately after."
 *
 * @param method  'google' | 'apple' | 'email' | 'passkey'
 * @param props   extra properties, such as the `ref` family
 * @param beacon  send with sendBeacon, for paths that navigate away
 */
export const trackAuthAttempted = (method, props = {}, beacon = false) => {
  return capturePostHogEvent(
    'auth_attempted',
    { method, ...props },
    beacon ? { send_instantly: true, transport: 'sendBeacon' } : undefined
  );
};

export const trackAuthSucceeded = (method, props = {}) => {
  capturePostHogEvent('auth_succeeded', { method, ...props });
};

export const trackAuthFailed = (method, errorCode, props = {}) => {
  capturePostHogEvent('auth_failed', { method, error_code: errorCode, ...props });
};

// ponytail: spec Tasks 24+25 - funnel events for the couple/household NUX +
// settings change. Mirrors existing `track*` helpers below.

export const trackHouseholdStepCompleted = (householdType, props = {}) => {
  capturePostHogEvent('onboarding_household_completed', {
    household_type: householdType,
    ...props,
  });
};

export const trackPartnerAboutYouCompleted = (props = {}) => {
  capturePostHogEvent('onboarding_partner_about_you_completed', props);
};

export const trackHouseholdTypeChanged = (from, to, props = {}) => {
  capturePostHogEvent('settings_household_type_changed', {
    from: from ?? null,
    to: to ?? null,
    ...props,
  });
};
